/**
 * ============================================================
 * 角色管理页面 (Roles)
 * ============================================================
 * 功能说明：
 *   - 角色CRUD（创建、编辑、删除）
 *   - 权限矩阵配置（每个角色对每个页面的查看/创建/编辑/删除权限）
 *   - 权限批量设置（全开/全关）
 *   - 默认角色保护（管理员/财务/经纪人不可删除）
 *   - Toast通知
 *
 * 原系统新增功能
 * ============================================================
 */

import { useEffect, useState, useCallback } from 'react';
import { roleApi } from '../api';
import type { Role, PermissionConfig } from '../types';
import { PAGE_MAP } from '../types';
import { Shield, Plus, Pencil, Trash2, X, RefreshCw, Check, XCircle } from 'lucide-react';

/** 所有可配置权限的页面 */
const ALL_PAGES = Object.entries(PAGE_MAP).map(([key, label]) => ({ key, label }));

/** 默认权限（全部关闭） */
function createDefaultPermissions(): PermissionConfig[] {
  return ALL_PAGES.map(p => ({ page: p.key, can_view: false, can_create: false, can_edit: false, can_delete: false }));
}

/** 管理员默认权限（全部开启） */
function createAdminPermissions(): PermissionConfig[] {
  return ALL_PAGES.map(p => ({ page: p.key, can_view: true, can_create: true, can_edit: true, can_delete: true }));
}

/** 受保护角色（不可删除） */
const PROTECTED_ROLES = ['管理员', '财务', '经纪人'];

export default function Roles() {
  // ========== 状态管理 ==========

  /** 角色列表 */
  const [roles, setRoles] = useState<Role[]>([]);
  /** 搜索关键词 */
  const [search, setSearch] = useState('');
  /** 加载状态 */
  const [loading, setLoading] = useState(true);
  /** 编辑弹窗 */
  const [showEdit, setShowEdit] = useState(false);
  /** 当前编辑的角色 */
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  /** 角色名称 */
  const [roleName, setRoleName] = useState('');
  /** 角色描述 */
  const [roleDesc, setRoleDesc] = useState('');
  /** 权限配置 */
  const [permissions, setPermissions] = useState<PermissionConfig[]>(createDefaultPermissions());
  /** 保存中 */
  const [saving, setSaving] = useState(false);
  /** Toast */
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ========== Toast 辅助 ==========
  const showSuccess = useCallback((message: string) => { setToast({ message, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((message: string) => { setToast({ message, type: 'error' }); setTimeout(() => setToast(null), 3000); }, []);

  // ========== 生命周期 ==========
  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await roleApi.list();
      setRoles(data);
    } catch (err: any) { showError(err.message || '加载失败'); }
    finally { setLoading(false); }
  };

  /** 将权限数据合并到默认权限模板中 */
  const mergePermissions = (perms: PermissionConfig[]) => {
    const newPerms = createDefaultPermissions();
    for (const p of perms) {
      const idx = newPerms.findIndex(np => np.page === p.page);
      if (idx >= 0) {
        newPerms[idx] = {
          page: p.page,
          can_view: !!p.can_view,
          can_create: !!p.can_create,
          can_edit: !!p.can_edit,
          can_delete: !!p.can_delete,
        };
      }
    }
    setPermissions(newPerms);
  };

  // ========== CRUD ==========

  const openEdit = async (role: Role | null) => {
    setEditingRole(role);
    if (role) {
      setRoleName(role.name);
      setRoleDesc(role.description || '');
      // 直接使用列表中已加载的 permissions 数据，避免二次 API 调用
      if (role.permissions && role.permissions.length > 0) {
        mergePermissions(role.permissions);
      } else {
        setPermissions(createDefaultPermissions());
      }
    } else {
      setRoleName('');
      setRoleDesc('');
      setPermissions(createDefaultPermissions());
    }
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!roleName.trim()) { showError('请输入角色名称'); return; }
    setSaving(true);
    try {
      const payload = { name: roleName, description: roleDesc, permissions };
      if (editingRole) {
        await roleApi.update(editingRole.id, payload);
        showSuccess('更新成功');
      } else {
        await roleApi.create(payload);
        showSuccess('创建成功');
      }
      setShowEdit(false);
      await loadRoles();
    } catch (err: any) { showError(err.message || '操作失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number, name: string) => {
    if (PROTECTED_ROLES.includes(name)) { showError('系统默认角色不可删除'); return; }
    if (!confirm('确定删除此角色吗？')) return;
    try { await roleApi.delete(id); showSuccess('删除成功'); await loadRoles(); }
    catch (err: any) { showError(err.message || '删除失败'); }
  };

  // ========== 权限操作 ==========

  const togglePerm = (page: string, action: 'can_view' | 'can_create' | 'can_edit' | 'can_delete') => {
    setPermissions(prev => prev.map(p => p.page === page ? { ...p, [action]: !p[action] } : p));
  };

  const setAllForPage = (page: string, value: boolean) => {
    setPermissions(prev => prev.map(p =>
      p.page === page ? { ...p, can_view: value, can_create: value, can_edit: value, can_delete: value } : p
    ));
  };

  // 一键全选所有权限
  const setAllPermissions = (value: boolean) => {
    setPermissions(prev => prev.map(p => ({
      ...p,
      can_view: value,
      can_create: value,
      can_edit: value,
      can_delete: value
    })));
  };

  // 一键设置所有页面的某个权限
  const setAllForAction = (action: 'can_view' | 'can_create' | 'can_edit' | 'can_delete', value: boolean) => {
    setPermissions(prev => prev.map(p => ({ ...p, [action]: value })));
  };

  // ========== 筛选 ==========
  const filteredRoles = roles.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase())
  );

  // ========== 渲染 ==========

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-6 h-6 text-purple-400" />
          角色管理
        </h1>
        <div className="flex gap-2">
          <button onClick={loadRoles}
            className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-all">
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
          <button onClick={() => openEdit(null)}
            className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm transition-all">
            <Plus className="w-4 h-4" /> 新建角色
          </button>
        </div>
      </div>

      {/* 角色卡片 */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredRoles.length === 0 ? (
        <div className="text-center py-16 text-[#94A3B8]">暂无角色</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRoles.map(role => (
            <div key={role.id} className="bg-[#1E293B] border border-[#334155] rounded-xl p-5 hover:border-purple-500/30 transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">{role.name}</h3>
                    <p className="text-xs text-[#94A3B8] mt-0.5">{role.description || '无描述'}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-[#334155]">
                <button onClick={() => openEdit(role)}
                  className="flex-1 py-2 bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 text-[#3B82F6] rounded text-xs flex items-center justify-center gap-1 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> 编辑权限
                </button>
                {!PROTECTED_ROLES.includes(role.name) && (
                  <button onClick={() => handleDelete(role.id, role.name)}
                    className="p-2 text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 权限编辑弹窗 */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEdit(false)}>
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingRole ? `编辑: ${editingRole.name}` : '新建角色'}</h3>
              <button onClick={() => setShowEdit(false)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">角色名称 *</label>
                <input value={roleName} onChange={e => setRoleName(e.target.value)}
                  className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="如: 财务专员" />
              </div>
              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">描述</label>
                <input value={roleDesc} onChange={e => setRoleDesc(e.target.value)}
                  className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="角色描述" />
              </div>
            </div>

            {/* 权限矩阵 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">页面权限配置</h3>
                <div className="flex gap-3">
                  <div className="flex gap-3 text-xs text-[#94A3B8]">
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> 允许</span>
                    <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-400" /> 禁止</span>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setAllPermissions(true)}
                      className="px-3 py-1 text-xs bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20">全选</button>
                    <button type="button" onClick={() => setAllPermissions(false)}
                      className="px-3 py-1 text-xs bg-red-500/10 text-red-400 rounded hover:bg-red-500/20">全不选</button>
                  </div>
                </div>
              </div>
              <div className="bg-[#0F172A] border border-[#334155] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#334155] bg-[#1E293B]">
                      <th className="px-4 py-2 text-left text-xs text-[#94A3B8] font-medium">页面</th>
                      <th className="px-4 py-2 text-center text-xs text-[#94A3B8] font-medium w-20">
                        <button type="button" onClick={() => setAllForAction('can_view', !permissions.every(p => p.can_view))}
                          className="text-[#3B82F6] hover:text-blue-300 underline decoration-dotted">查看</button>
                      </th>
                      <th className="px-4 py-2 text-center text-xs text-[#94A3B8] font-medium w-20">
                        <button type="button" onClick={() => setAllForAction('can_create', !permissions.every(p => p.can_create))}
                          className="text-[#3B82F6] hover:text-blue-300 underline decoration-dotted">创建</button>
                      </th>
                      <th className="px-4 py-2 text-center text-xs text-[#94A3B8] font-medium w-20">
                        <button type="button" onClick={() => setAllForAction('can_edit', !permissions.every(p => p.can_edit))}
                          className="text-[#3B82F6] hover:text-blue-300 underline decoration-dotted">编辑</button>
                      </th>
                      <th className="px-4 py-2 text-center text-xs text-[#94A3B8] font-medium w-20">
                        <button type="button" onClick={() => setAllForAction('can_delete', !permissions.every(p => p.can_delete))}
                          className="text-[#3B82F6] hover:text-blue-300 underline decoration-dotted">删除</button>
                      </th>
                      <th className="px-4 py-2 text-center text-xs text-[#94A3B8] font-medium w-24">批量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_PAGES.map(({ key, label }) => {
                      const perm = permissions.find(p => p.page === key);
                      if (!perm) {
                        console.warn(`[Roles] 未找到页面 ${key} 的权限配置`);
                        return null;
                      }
                      return (
                        <tr key={key} className="border-b border-[#334155] last:border-0">
                          <td className="px-4 py-2 font-medium">{label}</td>
                          {(['can_view', 'can_create', 'can_edit', 'can_delete'] as const).map(action => (
                            <td key={action} className="px-4 py-2 text-center">
                              <button type="button" onClick={() => togglePerm(key, action)}
                                className={`w-6 h-6 rounded flex items-center justify-center mx-auto transition-colors ${
                                  perm[action] ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/10 text-red-400'
                                }`}>
                                {perm[action] ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                              </button>
                            </td>
                          ))}
                          <td className="px-4 py-2 text-center">
                            <div className="flex justify-center gap-1">
                              <button type="button" onClick={() => setAllForPage(key, true)}
                                className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 rounded hover:bg-emerald-500/20">全开</button>
                              <button type="button" onClick={() => setAllForPage(key, false)}
                                className="px-2 py-0.5 text-xs bg-red-500/10 text-red-400 rounded hover:bg-red-500/20">全关</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={() => setShowEdit(false)} className="h-10 px-4 border border-[#334155] rounded-lg text-sm hover:bg-[#334155]/50">取消</button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="h-10 px-4 bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg text-sm disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
